"use client";

import { useCartStore } from "@/lib/cart-store";
import { MemberAvatarImg } from "@/components/site/MemberAvatar";

import { HUB_NAV_ITEMS } from "./hubNavIcons";
import { hudNavHref } from "./hub-nav";
import { RESORTS } from "./resorts";
import { openMountainSelect } from "./MountainSelectLayer";

// Phase-1b PORT (strangler-fig): the live snowbus.com MOUNTAIN-HUB header variant — the third header the live
// site serves (F26), distinct from home's `homeHeader` (SiteHeader) and the info-page "Select your mountain"
// bar (SiteHeaderContent). Markup + classes reproduce the live hub header 1:1 so the inlined live CSSOM styles
// it (pixel parity holds); the "Change mountain" control is REAL React state — the mirror's JS-stripped
// dropdown was dead (W4/W5). Desktop only; mobile stays mirror (F30, as browse/detail). The per-mountain badge
// (name + S3 logo) is passed in, extracted at render time from the captured header (page.tsx). The nav buttons
// (marketplace/transport/directory + live-disabled flights/stays "Coming Soon") reproduce live; their actions
// are systems queued for S2/S3 (inert here). Cart badge is inert; profile/sign-in are wired in the W6 step.
const LOGO = "/_next/static/media/headerLogoNew2.713156d5.svg";
const CHEVRON = "/_mirror/_next/static/media/blueIcon.f4019d62.svg";
const CART_ICON = "/_next/static/media/openCartIcon.f3aa487e.svg";
const PROFILE_ICON = "/_next/static/media/profileIcon.4bf36b73.svg";

export default function SiteHeaderHub({
  mountainName,
  badgeSrc,
  mountainCode,
}: {
  mountainName: string;
  badgeSrc: string;
  mountainCode: string;
}) {
  const setAuthOpen = useCartStore((s) => s.setAuthOpen); // W6 — profile icon opens the login popup

  // S26 · A3 — the local change-mountain dropdown (and with it the whole S12 · RW2-5 portal/anchor/
  // outside-click apparatus that existed only to stop the header's overflow:hidden ancestors clipping a
  // 320px menu) is REPLACED by the one site-wide R1a selector. A full-viewport fixed overlay cannot be
  // clipped by an ancestor at all, so the class of bug RW2-5 fixed cannot recur here. Nothing renders at
  // rest, so the 30 landings' at-rest pixel baselines are untouched exactly as before.
  return (
    <header className="MuiPaper-root MuiPaper-elevation MuiPaper-elevation0 MuiAppBar-root MuiAppBar-colorDefault MuiAppBar-positionSticky css-1j5e5rn">
      <div className="MuiToolbar-root MuiToolbar-gutters MuiToolbar-regular css-2u379j">
        <div className="MuiBox-root css-166albc">
          <a aria-label="Home" href="/">
            <img
              alt="Logo"
              width={49}
              height={56}
              style={{
                color: "transparent",
                height: "40px",
                width: "auto",
                cursor: "pointer",
              }}
              src={LOGO}
            />
          </a>
        </div>
        <div className="MuiBox-root css-fmy3cc">
          <div className="MuiBox-root css-b3n6tb">
            <div className="MuiBox-root css-6su6fj">
              {/* Change-mountain trigger — real React state (the mirror's dropdown was dead). */}
              <div
                className="MuiBox-root css-opckaj"
                data-behavior="nav-toggle"
                role="button"
                aria-label="Change mountain"
                // S26 — it opens a DIALOG now, not an inline menu, so `aria-haspopup="dialog"` is the
                // truthful relationship; `aria-expanded` would have to lie (the open state lives in the
                // layer, not here).
                aria-haspopup="dialog"
                // S26 · A3 — this control now opens the ONE site-wide R1a selector
                // (`MountainSelectLayer`) instead of its own 30-row dropdown. Neutral entry point → no
                // mode → the mountain HUB, exactly the destination the dropdown resolved (F89 R1), so
                // where a pick lands is unchanged; only the selector the visitor sees is.
                // Founder QA 2026-09-24 — open the integrated map selector directly.
                onClick={() => openMountainSelect(null)}
                style={{ cursor: "pointer" }}
              >
                <div className="MuiBox-root css-1npozt1">
                  <div className="MuiBox-root css-btdhl8">
                    <img
                      alt={`${mountainName} logo`}
                      width={240}
                      height={120}
                      src={badgeSrc}
                      style={{
                        color: "transparent",
                        width: "100%",
                        height: "auto",
                        display: "block",
                      }}
                    />
                  </div>
                </div>
                <div className="MuiBox-root css-16yngqg">
                  <img
                    alt="Change mountain"
                    width={16}
                    height={16}
                    src={CHEVRON}
                    style={{
                      color: "transparent",
                      width: "16px",
                      height: "16px",
                      marginRight: "3px",
                      transition: "transform 0.3s",
                    }}
                  />
                </div>
              </div>
              {/* S26 · A3 — the local 30-row dropdown is GONE; `MountainSelectLayer` renders the one R1a
                  selector for this surface. The RW2-5 clipping fix it carried is inherited: the modal is a
                  fixed full-viewport overlay, so no header ancestor can clip it. Still nothing at rest. */}
              {/* hidden SEO resort sitemap (live parity — link equity to the 30 mountain pages) */}
              <nav
                aria-label="Resorts sitemap"
                style={{ display: "none" }}
                className="resort-sitemap"
              >
                <ul>
                  {RESORTS.map((r) => (
                    <li key={r.href}>
                      <a href={r.href}>{r.label}</a>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
            <div className="MuiBox-root css-4e43x">
              <div className="MuiBox-root css-1aie9hu">
                {HUB_NAV_ITEMS.map((it, i) => {
                  const href = it.disabled ? null : hudNavHref(mountainCode, it.label);
                  const inner = (
                    <>
                      <div
                        className="MuiBox-root css-1m0mia4"
                        dangerouslySetInnerHTML={{ __html: it.svg }}
                      />
                      <p className={it.labelCls}>{it.label}</p>
                      {!it.disabled && (
                        <span className="MuiTouchRipple-root css-w0pj6f" />
                      )}
                    </>
                  );
                  return (
                    <div
                      key={i}
                      className="MuiBox-root css-jma0kd"
                      aria-label={it.ariaLabel || undefined}
                    >
                      {/* W5/P0 — enabled HUD items are real per-mountain links (MUI button classes kept →
                          pixel-neutral vs the captured <button>); flights/stays stay disabled buttons. */}
                      {href ? (
                        <a
                          className={it.buttonClass}
                          href={href}
                          data-behavior="hud-nav"
                        >
                          {inner}
                        </a>
                      ) : (
                        <button
                          className={it.buttonClass}
                          type="button"
                          disabled={it.disabled}
                          tabIndex={it.disabled ? -1 : 0}
                        >
                          {inner}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="MuiBox-root css-1cqx21f">
              <button
                className="MuiButtonBase-root MuiIconButton-root MuiIconButton-colorInherit MuiIconButton-sizeMedium css-1sxc1sc"
                type="button"
                aria-label="Open cart"
              >
                <span className="MuiBadge-root css-oqf3hq">
                  <img
                    alt=""
                    width={32}
                    height={24}
                    style={{
                      color: "transparent",
                      display: "block",
                      width: "32px",
                      height: "24px",
                      transform: "translateY(-1px)",
                    }}
                    src={CART_ICON}
                  />
                  <span className="MuiBadge-badge MuiBadge-standard MuiBadge-invisible MuiBadge-anchorOriginTopRight MuiBadge-anchorOriginTopRightRectangular MuiBadge-overlapRectangular MuiBadge-colorError css-c6vaoj">
                    0
                  </span>
                </span>
                <span className="MuiTouchRipple-root css-w0pj6f" />
              </button>
            </div>
          </div>
        </div>
        <div className="MuiBox-root css-ss5hdk">
          <button
            className="MuiButtonBase-root MuiIconButton-root MuiIconButton-colorInherit MuiIconButton-sizeMedium css-1deacqj"
            type="button"
            data-behavior="signin-open"
            aria-label="Sign in"
            onClick={() => setAuthOpen(true)}
          >
            <MemberAvatarImg alt="Profile" width={32} height={32} style={{ color: "transparent" }} fallback={PROFILE_ICON} />
            <span className="MuiTouchRipple-root css-w0pj6f" />
          </button>
        </div>
      </div>
    </header>
  );
}
